import { validate as validateAddress } from '@dartess/multicoin-address-validator';
import { validate } from 'crypto-address-validators';
import moment from 'moment';
import validator from 'validator';
import { CountryTypes } from '@/types/country';
import { TransactTypes } from '@/types/transact';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
var CountryCodes = CountryTypes.CountryCodes;
var Countries = CountryTypes.Countries;
var ChannelTypes = WidgetApiServiceTypes.ChannelTypes;
export const getTransactionChannelType = (transaction) => {
    const accountType = transaction.bankInfo?.paymentLink
        ? 'eft'
        : transaction.source?.accountType.toLowerCase();
    return Object.values(ChannelTypes).find(value => value.toLowerCase() === accountType);
};
export const formatDate = (d, format = 'LLL') => moment(d).format(format);
export const numberFormat = (number, maximumFractionDigits = 2, minimumFractionDigits = 0) => {
    if (!number) {
        return '0.00';
    }
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(number);
};
export const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const seconds = time % 60;
    if (hours > 0) {
        const minutes = Math.floor((time % 3600) / 60);
        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}s`;
    }
    const minutes = Math.floor(time / 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}s`;
};
export const titleCase = (str) => {
    if (!str)
        return str;
    const words = str.toLowerCase().split(' ');
    return words
        .map(word => word[0].toUpperCase().concat(word.slice(1)))
        .join(' ');
};
export function getChannelLabel(channelType) {
    switch (channelType) {
        case WidgetApiServiceTypes.ChannelTypes.BANK:
        case WidgetApiServiceTypes.ChannelTypes.EFT:
        case WidgetApiServiceTypes.ChannelTypes.P2P:
            return 'Bank';
        case WidgetApiServiceTypes.ChannelTypes.MOMO:
            return 'Mobile Money';
        default:
            return '';
    }
}
export function getChannelType(channelType) {
    switch (channelType) {
        case WidgetApiServiceTypes.ChannelTypes.BANK:
        case WidgetApiServiceTypes.ChannelTypes.EFT:
        case WidgetApiServiceTypes.ChannelTypes.P2P:
            return 'bank';
        case WidgetApiServiceTypes.ChannelTypes.MOMO:
            return 'momo';
        default:
            return '';
    }
}
export function getCountryCodeFromCountry(countryName) {
    const countryKey = Object.keys(Countries).find(key => Countries[key] === countryName);
    if (countryKey) {
        return CountryCodes[countryKey];
    }
    return undefined;
}
export function streetIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z0-9_\-\s.,:;@!]+$/;
    return !legalCharactersPattern.test(input);
}
export function nameIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z\-\s]+$/;
    return !legalCharactersPattern.test(input);
}
export function postalCodeIsInvalid(input) {
    const legalCharactersPattern = /^[a-zA-Z0-9_\-\s:]+$/;
    return !legalCharactersPattern.test(input);
}
export function IdIsInvalid(input, regex, flag) {
    if (!regex)
        return false;
    const expression = new RegExp(regex, flag);
    return !expression.test(input);
}
export function validateMobileNumber(value, minLength, maxLength) {
    // TODO this needs to be relooked at
    const MIN_PHONE_LENGTH = minLength ?? 7;
    const MAX_PHONE_LENGTH = maxLength ?? 10;
    if (!/^\d+$/.test(value))
        return ' ';
    if (value.startsWith('0') && value.length < MAX_PHONE_LENGTH) {
        return ' ';
    }
    if (value.length < MIN_PHONE_LENGTH || value.length > MAX_PHONE_LENGTH) {
        return ' ';
    }
    return true;
}
export const isValidEmail = (email) => validator.isEmail(email);
export const maskString = (str, maskChar = '*', totalLength = 8, visibleChars = 4) => {
    if (str.length >= totalLength) {
        return (maskChar.repeat(totalLength - visibleChars) + str.slice(-visibleChars));
    }
    const padding = totalLength - str.length;
    return maskChar.repeat(padding) + str;
};
export const addCountryPrefix = (phoneNumber, countryCode) => {
    if (phoneNumber.startsWith(countryCode)) {
        return phoneNumber;
    }
    return `${countryCode}${phoneNumber}`;
};
export const removeCountryPrefix = (phoneNumber, countryCode) => {
    if (phoneNumber.startsWith(countryCode)) {
        return phoneNumber.slice(countryCode.length);
    }
    return phoneNumber;
};
export const validateAddressByRegex = (regex, address) => {
    const expression = new RegExp(regex);
    return !expression.test(address);
};
export const validateXRPAddressByRegex = (address) => /^r[0-9a-zA-Z]{24,34}$/.test(address);
export function isValidWalletAddress(wallet) {
    const { address, networkType, standard, token } = wallet;
    const _standard = standard?.toLocaleUpperCase();
    const { ERC20, TRC20 } = TransactTypes.Network;
    switch (token) {
        case 'USDT':
            if (['CELO', 'ERC20', 'POLYGON'].includes(_standard)) {
                return validate(address, 'ETH', ERC20);
            }
            if (_standard === 'TON') {
                // TODO move regex into proper config when we update partner-rates to hold wallet validation regex
                return validateAddressByRegex('^(EQ|UQ)[A-Za-z0-9_-]{46}$', address);
            }
            if (_standard === 'SOL') {
                return validateAddress(address, 'solana');
            }
            return (validate(address, token, TRC20) && /T[A-Za-z1-9]{33}/.test(address));
        case 'ETH':
            return validate(address, token, ERC20);
        case 'PYUSD':
            return validate(address, 'eth');
        case 'XAUT':
            switch (networkType) {
                case ERC20:
                    return validate(address, 'eth');
                case TRC20:
                    return validate(address, 'tron');
                default:
                    return false;
            }
        case 'SOL':
            return validateAddress(address, 'solana');
        case 'ADA':
            return validateAddress(address, 'ada');
        case 'MATIC':
            return validateAddress(address, 'matic');
        case 'BTC':
            return validate(address, token);
        case 'USDC':
            if (_standard === 'SOL') {
                return validateAddress(address, 'solana');
            }
            if (['CELO', 'ERC20', 'POLYGON'].includes(_standard)) {
                return validate(address, 'ETH', ERC20);
            }
            return validateAddress(address, token);
        case 'CUSD':
            if (['CELO', 'ERC20'].includes(_standard)) {
                return validate(address, 'ETH', ERC20);
            }
            return validateAddress(address, token);
        case 'TON':
            return validateAddressByRegex('^(EQ|UQ)[A-Za-z0-9_-]{46}$', address);
        case 'RLUSD':
            return validateXRPAddressByRegex(address);
        default:
            return false;
    }
}
export const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
export function validateUrl(url) {
    if (!url)
        return false;
    try {
        const parsedURL = new URL(url);
        const allowedProtocols = ['https:'];
        if (!allowedProtocols.includes(parsedURL.protocol.toLowerCase())) {
            return false;
        }
        if (!parsedURL.hostname) {
            return false;
        }
        if (/javascript:/i.test(url)) {
            return false;
        }
        const securityChecks = {
            noDataProtocol: !url.toLowerCase().includes('data:'),
            noVBScriptProtocol: !url.toLowerCase().includes('vbscript:'),
            noFileProtocol: !url.toLowerCase().includes('file:'),
            noScriptTags: !/<script.*?>.*?<\/script>/i.test(url),
            noOnHandlers: !/(on\w+\s*=)/i.test(url),
            noSuspiciousEncoding: !/(%0[0-9A-Fa-f]|%1[0-9A-Fa-f]|%2[0-9A-Fa-f]|%3[0-9A-Fa-f])/i.test(url),
        };
        if (Object.values(securityChecks).includes(false)) {
            return false;
        }
        // Additional URL structure validation
        const urlPattern = /^https:\/\/(?:[a-zA-Z\d](?:[a-zA-Z\d-]{0,61}[a-zA-Z\d])?\.)+[a-zA-Z]{2,6}(?::\d{1,5})?(?:\/[^\s]*)?$/;
        return urlPattern.test(url);
    }
    catch (error) {
        return false;
    }
}
